/**
 * Delete Rides Hook
 * 
 * Custom hook for handling ride delete/cancel operations with confirmation, batch processing,
 * progress tracking, and error handling.
 * 
 * @module useDeleteRides
 */

import { useState, useCallback } from 'react';
import { cancelRide } from '../services/rideUatClient';
import type { Environment } from '../services/fixedRouteUatClient';
import { handleApiError } from '../utils/errorHandling';
import type { RouteOperationResult } from '../components/map/components/routes-transfer/OperationResults';
import type { UATRideEntry, UATRideDetailedEntry } from '../services/fixedRouteUatClient';

/**
 * Delete progress for a single ride
 */
export interface RideDeleteProgress {
  rideId: string;
  rideName: string;
  status: 'pending' | 'deleting' | 'success' | 'error';
  message: string;
  progress?: number; // 0-100
}

/**
 * Delete state
 */
export interface DeleteState {
  /** Whether delete is in progress */
  isDeleting: boolean;
  /** Delete progress for each ride */
  progress: Map<string, RideDeleteProgress>;
  /** Delete results */
  results: RouteOperationResult[];
  /** Current error */
  error: string | null;
}

/**
 * Hook return type
 */
export interface UseDeleteRidesReturn {
  /** Delete state */
  deleteState: DeleteState;
  /** Start delete operation */
  startDelete: (
    selectedRideIds: Set<string>,
    selectedRides: (UATRideEntry | UATRideDetailedEntry)[],
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkUuid: string;
      environment: Environment;
    }
  ) => Promise<void>;
  /** Reset delete state */
  resetDelete: () => void;
}

/**
 * useDeleteRides Hook
 * 
 * Manages ride delete operations with batch processing and progress tracking.
 */
export function useDeleteRides(): UseDeleteRidesReturn {
  const [deleteState, setDeleteState] = useState<DeleteState>({
    isDeleting: false,
    progress: new Map(),
    results: [],
    error: null,
  });

  /**
   * Start delete operation
   */
  const startDelete = useCallback(
    async (
      selectedRideIds: Set<string>,
      selectedRides: (UATRideEntry | UATRideDetailedEntry)[],
      prerequisites: {
        jwtToken: string;
        productId: string;
        networkUuid: string;
        environment: Environment;
      }
    ) => {
      if (selectedRideIds.size === 0 || selectedRides.length === 0) {
        return;
      }

      // Validate prerequisites
      if (!prerequisites.jwtToken || !prerequisites.jwtToken.trim()) {
        setDeleteState(prev => ({
          ...prev,
          error: 'JWT token is required',
        }));
        return;
      }

      if (!prerequisites.productId) {
        setDeleteState(prev => ({
          ...prev,
          error: 'Product ID is required',
        }));
        return;
      }

      if (!prerequisites.networkUuid) {
        setDeleteState(prev => ({
          ...prev,
          error: 'Network UUID is required',
        }));
        return;
      }

      // Initialize progress
      const progressMap = new Map<string, RideDeleteProgress>();
      selectedRides.forEach((ride) => {
        // Extract ride name from segments or use ID
        let rideName = ride.id;
        if ('segments' in ride && ride.segments && ride.segments.length > 0) {
          const pickupSegment = ride.segments.find(s => s.activity === 'pickup');
          const dropoffSegment = ride.segments.find(s => s.activity === 'dropoff');
          const pickup = pickupSegment?.address?.label || 'Pickup';
          const dropoff = dropoffSegment?.address?.label || 'Dropoff';
          rideName = `${pickup} → ${dropoff}`;
        }

        progressMap.set(ride.id, {
          rideId: ride.id,
          rideName,
          status: 'pending',
          message: 'Waiting to delete...',
          progress: 0,
        });
      });

      setDeleteState({
        isDeleting: true,
        progress: progressMap,
        results: [],
        error: null,
      });

      const results: RouteOperationResult[] = [];

      // Delete rides sequentially
      for (let i = 0; i < selectedRides.length; i++) {
        const ride = selectedRides[i];
        const progress = progressMap.get(ride.id)!;

        try {
          // Update progress: deleting
          progress.status = 'deleting';
          progress.message = 'Deleting...';
          progress.progress = ((i + 0.5) / selectedRides.length) * 100;
          setDeleteState((prev) => ({
            ...prev,
            progress: new Map(progressMap),
          }));

          // Call API
          const response = await cancelRide(
            prerequisites.jwtToken,
            prerequisites.productId,
            prerequisites.networkUuid,
            ride.id,
            prerequisites.environment
          );

          // Check if deletion was successful
          if (response.success) {
            // Update progress: success
            progress.status = 'success';
            progress.message = response.message || 'Deleted successfully';
            progress.progress = ((i + 1) / selectedRides.length) * 100;
            setDeleteState((prev) => ({
              ...prev,
              progress: new Map(progressMap),
            }));

            results.push({
              routeId: ride.id,
              routeName: progress.rideName,
              success: true,
            });
          } else {
            // Update progress: error
            progress.status = 'error';
            progress.message = response.error || 'Failed to delete';
            progress.progress = ((i + 1) / selectedRides.length) * 100;
            setDeleteState((prev) => ({
              ...prev,
              progress: new Map(progressMap),
            }));

            results.push({
              routeId: ride.id,
              routeName: progress.rideName,
              success: false,
              error: response.error || 'Failed to delete ride',
            });
          }
        } catch (error) {
          const friendlyError = handleApiError(error, `Delete ride "${progress.rideName}"`);

          // Update progress: error
          progress.status = 'error';
          progress.message = friendlyError.message;
          progress.progress = ((i + 1) / selectedRides.length) * 100;
          setDeleteState((prev) => ({
            ...prev,
            progress: new Map(progressMap),
          }));

          results.push({
            routeId: ride.id,
            routeName: progress.rideName,
            success: false,
            error: friendlyError.message,
          });
        }
      }

      // Finalize
      setDeleteState((prev) => ({
        ...prev,
        isDeleting: false,
        results,
      }));
    },
    []
  );

  /**
   * Reset delete state
   */
  const resetDelete = useCallback(() => {
    setDeleteState({
      isDeleting: false,
      progress: new Map(),
      results: [],
      error: null,
    });
  }, []);

  return {
    deleteState,
    startDelete,
    resetDelete,
  };
}
